// SOC 2 Type II — Trust Services Criteria explanations
const SOC2_EXPLANATIONS = {
  'CC1.1': {
    plain_english: 'Leadership must visibly commit to honesty and ethical behavior, set the tone for the organization, and enforce a code of conduct consistently across all levels.',
    example: 'The CEO signs off on a published Code of Ethics policy. Violations result in documented disciplinary action. All employees sign the code annually.',
    evidence_tips: [
      'Signed Code of Ethics or Code of Conduct policy with revision history',
      'Training completion records showing all employees acknowledged the policy',
      'Documentation of disciplinary actions taken for policy violations',
    ],
    common_mistakes: 'Having a written code of conduct that is never enforced — leadership tolerates exceptions for high performers, undermining the entire control environment.',
    nist_reference: 'CC1.1',
  },
  'CC1.2': {
    plain_english: 'An independent board or equivalent oversight body must hold management accountable for security, not just rubber-stamp decisions. They need enough independence to push back.',
    example: 'An audit committee of independent board members reviews the CISO\'s quarterly security report, asks questions about open vulnerabilities, and approves the security budget.',
    evidence_tips: [
      'Board or audit committee charter defining independence requirements',
      'Minutes from board/audit committee meetings showing security topics reviewed',
      'CISO or security leadership reports presented to the board',
    ],
    common_mistakes: 'The "board" consists entirely of company insiders with financial conflicts of interest, providing no meaningful independent oversight of security practices.',
    nist_reference: 'CC1.2',
  },
  'CC1.3': {
    plain_english: 'The organization has clear reporting lines, defined authorities, and documented responsibilities so everyone knows who is accountable for security decisions.',
    example: 'An org chart shows the CISO reporting to the CEO with a dotted line to the audit committee. Role descriptions for IT, security, and operations staff explicitly list security responsibilities.',
    evidence_tips: [
      'Organizational chart showing reporting lines for security leadership',
      'Job descriptions with security responsibilities for key roles',
      'RACI matrix mapping security activities to specific roles',
    ],
    common_mistakes: 'Security responsibilities are vague — everyone thinks someone else owns a decision, so critical security tasks fall through the cracks.',
    nist_reference: 'CC1.3',
  },
  'CC1.4': {
    plain_english: 'The organization hires, trains, and retains staff with the skills needed to operate security controls effectively. Competency gaps must be identified and addressed.',
    example: 'HR uses defined competency requirements when hiring for the security operations center. New SOC analysts complete a 90-day onboarding program before monitoring live alerts independently.',
    evidence_tips: [
      'Job descriptions with required security certifications or skills for key roles',
      'Training plans and completion records for security staff',
      'Performance review documentation that includes assessment of security competencies',
    ],
    common_mistakes: 'Security tools are deployed but operated by staff who haven\'t been trained on them, resulting in misconfigured controls and missed alerts.',
    nist_reference: 'CC1.4',
  },
  'CC1.5': {
    plain_english: 'Individuals are held accountable for their security responsibilities through performance evaluations, and there are real consequences for non-compliance.',
    example: 'Security responsibilities are included in annual performance reviews. An employee who repeatedly ignores phishing awareness training receives a documented corrective action.',
    evidence_tips: [
      'Performance review templates showing security responsibility evaluation',
      'Documented disciplinary actions for security policy violations',
      'Records showing consequences were applied consistently',
    ],
    common_mistakes: 'Security is listed on job descriptions and performance plans but never actually evaluated — there are no consequences for ignoring security responsibilities.',
    nist_reference: 'CC1.5',
  },
  'CC2.1': {
    plain_english: 'The organization generates and uses high-quality, relevant information to make security decisions and run controls effectively, including proper information flows within the organization.',
    example: 'The security team receives daily vulnerability scan reports, weekly threat intelligence briefings, and monthly risk dashboards. These feed into prioritization decisions for remediation.',
    evidence_tips: [
      'Examples of security reports and dashboards used in decision-making',
      'Evidence that threat intelligence feeds are subscribed to and reviewed',
      'Documentation of information flow diagrams showing how security data moves through the organization',
    ],
    common_mistakes: 'Security data is collected but nobody reviews it — dashboards exist but decisions are made based on gut feel rather than the available information.',
    nist_reference: 'CC2.1',
  },
  'CC2.2': {
    plain_english: 'Security policies, responsibilities, and expectations are clearly communicated to all employees so everyone understands what is expected of them.',
    example: 'New employees receive a security awareness briefing on day one. The intranet hosts all security policies. Annual all-hands meetings include a security update from the CISO.',
    evidence_tips: [
      'Security policy communication records (email, intranet, training systems)',
      'Employee acknowledgment records for key security policies',
      'New hire security orientation curriculum and completion records',
    ],
    common_mistakes: 'Security policies exist in a folder that nobody reads — employees are unaware of their obligations because they were never effectively communicated.',
    nist_reference: 'CC2.2',
  },
  'CC2.3': {
    plain_english: 'The organization proactively communicates with customers, partners, and other external parties about security commitments, system changes, and anything that affects them.',
    example: 'When a planned maintenance window will cause service downtime, customers receive 72-hour advance notice via email. When a security incident occurs, affected customers are notified within 24 hours.',
    evidence_tips: [
      'Customer notification templates and records of communications sent',
      'Terms of service or trust page describing security commitments',
      'Evidence of proactive breach or incident notifications to affected parties',
    ],
    common_mistakes: 'The company discovers an incident affecting customer data but delays notification for weeks while investigating internally, violating notification commitments.',
    nist_reference: 'CC2.3',
  },
  'CC2.4': {
    plain_english: 'The organization actively seeks feedback and information from both internal sources and external sources like threat intel feeds, audit findings, and customer reports to continuously improve.',
    example: 'After a penetration test uncovers a configuration weakness, findings are reviewed by the security committee and fed into the remediation backlog. Threat intel from an ISAC informs firewall rule updates.',
    evidence_tips: [
      'Evidence of threat intelligence subscriptions and how findings are acted upon',
      'Audit finding tracking records showing remediation of identified issues',
      'Meeting minutes or records showing external feedback was reviewed and acted upon',
    ],
    common_mistakes: 'External audit findings are documented but never remediated — the same issues appear in successive audits because there is no follow-through process.',
    nist_reference: 'CC2.4',
  },
  'CC3.1': {
    plain_english: 'The organization clearly defines its security and operational objectives so risks can be identified against them. You cannot assess risk without knowing what you are trying to protect and achieve.',
    example: 'The organization documents that its primary objective is 99.9% SaaS uptime and protection of customer PII. Risk assessments are conducted against these defined objectives.',
    evidence_tips: [
      'Documented security and business objectives approved by leadership',
      'Risk assessment methodology document that references defined objectives',
      'Strategic planning documents that articulate what the organization must achieve',
    ],
    common_mistakes: 'Risk assessments are conducted in a vacuum with no reference to business objectives — the results are generic and not actionable for the specific organization.',
    nist_reference: 'CC3.1',
  },
  'CC3.2': {
    plain_english: 'The organization systematically identifies and analyzes risks to its objectives from all sources — internal, external, and operational — and determines how to respond.',
    example: 'An annual risk assessment identifies 15 risks including ransomware, third-party data breach, and key person dependency. Each risk is scored for likelihood and impact, with treatment decisions documented.',
    evidence_tips: [
      'Formal risk assessment report with identified threats, likelihood, and impact ratings',
      'Risk register tracking identified risks, owners, and treatment decisions',
      'Evidence of management review and approval of the risk assessment',
    ],
    common_mistakes: 'Risk assessments are performed once at the start and never updated, missing new threats introduced by system changes, new products, or evolving attack techniques.',
    nist_reference: 'CC3.2',
  },
  'CC3.3': {
    plain_english: 'When assessing risks, the organization specifically considers the possibility of fraud — including unauthorized access, data manipulation, and theft of assets.',
    example: 'The annual risk assessment includes a fraud risk section that evaluates the risk of insider account manipulation in the billing system and mitigates it with dual-approval workflows.',
    evidence_tips: [
      'Risk assessment documentation that explicitly includes a fraud risk section',
      'Anti-fraud controls documentation (separation of duties, dual approvals)',
      'Internal audit or review records addressing fraud risk scenarios',
    ],
    common_mistakes: 'Risk assessments focus only on external cyber threats and ignore insider fraud — internal manipulation of systems or data is never assessed.',
    nist_reference: 'CC3.3',
  },
  'CC3.4': {
    plain_english: 'The organization stays current with changes — regulatory, technology, business model — that could affect the control environment and adjusts its risk assessment accordingly.',
    example: 'When the organization launches a new payment feature, the risk assessment is updated to include payment fraud risks before the feature goes live.',
    evidence_tips: [
      'Records showing risk assessments were updated following significant changes',
      'Change management process that triggers risk re-assessment for major changes',
      'Documentation of regulatory change monitoring process',
    ],
    common_mistakes: 'A significant system change like moving to the cloud is made without reassessing risk — the existing controls were designed for on-premises infrastructure and are no longer appropriate.',
    nist_reference: 'CC3.4',
  },
  'CC3.5': {
    plain_english: 'After implementing risk treatment, the organization verifies that residual risks are within acceptable tolerance and that the mitigations actually worked.',
    example: 'After deploying MFA to address unauthorized access risk, the security team re-scores the risk and confirms it now falls below the organization\'s risk tolerance threshold.',
    evidence_tips: [
      'Risk re-assessment records after control implementation showing residual risk scoring',
      'Risk acceptance documentation signed by management for accepted risks',
      'Evidence that risk treatment effectiveness was verified through testing or monitoring',
    ],
    common_mistakes: 'Controls are implemented in response to risks but never tested — there is no verification that the control actually reduced the risk to an acceptable level.',
    nist_reference: 'CC3.5',
  },
  'CC4.1': {
    plain_english: 'The organization regularly evaluates whether security controls are working as intended through ongoing monitoring, periodic assessments, and internal audits.',
    example: 'The internal audit team conducts quarterly reviews of access control effectiveness, including sampling 20 user accounts to verify least-privilege is enforced.',
    evidence_tips: [
      'Internal audit plan and completed audit reports covering security controls',
      'Ongoing monitoring evidence (SIEM alerts, control dashboards, access reviews)',
      'Evidence that assessment findings were reported to management',
    ],
    common_mistakes: 'Controls are assumed to be working because they were configured correctly at deployment — no ongoing evaluation is performed, allowing drift and failures to go undetected.',
    nist_reference: 'CC4.1',
  },
  'CC4.2': {
    plain_english: 'When control deficiencies are identified, they are communicated to the right people quickly and remediated — deficiencies are not buried or ignored.',
    example: 'An internal audit finding that MFA was not enforced for 12 contractor accounts is escalated to the CISO and IT director within 48 hours, with a 14-day remediation deadline.',
    evidence_tips: [
      'Deficiency tracking log showing open findings, owners, and remediation dates',
      'Evidence that findings were communicated to responsible parties',
      'Records showing remediation was verified after completion',
    ],
    common_mistakes: 'Audit findings are documented in a report that management acknowledges and files away — no tracking system ensures the findings are actually remediated.',
    nist_reference: 'CC4.2',
  },
  'CC5.1': {
    plain_english: 'The organization selects and deploys controls that are appropriate for mitigating its identified risks — both preventive controls that stop bad things from happening and detective controls that catch problems.',
    example: 'To address the risk of SQL injection in the web application, the organization deploys a WAF (preventive) and conducts quarterly code reviews (detective).',
    evidence_tips: [
      'Risk treatment plan showing controls selected to address each identified risk',
      'Evidence of both preventive and detective controls implemented',
      'Control mapping documentation linking controls to specific risks',
    ],
    common_mistakes: 'Organizations focus entirely on perimeter defenses (preventive) without any detective controls — breaches go undetected because there is nothing looking for them inside the network.',
    nist_reference: 'CC5.1',
  },
  'CC5.2': {
    plain_english: 'The organization deploys general IT controls covering access management, change control, and operations, which form the foundation for all other security controls.',
    example: 'Access management uses Active Directory with role-based groups. Changes go through a formal CAB approval process. System operations are monitored via a SIEM with defined alert thresholds.',
    evidence_tips: [
      'Access management policy and evidence of role-based access control implementation',
      'Change management policy and approval records showing change control in action',
      'IT operations procedures covering backup, monitoring, and incident response',
    ],
    common_mistakes: 'General IT controls are ignored because they are "basic IT," leaving a weak foundation that undermines all higher-level security controls built on top of it.',
    nist_reference: 'CC5.2',
  },
  'CC5.3': {
    plain_english: 'Security controls are implemented through documented policies and step-by-step procedures that put those policies into action, and they are reviewed and updated regularly.',
    example: 'The access provisioning control is documented as both a high-level policy ("access follows least privilege") and a detailed procedure ("submit ServiceNow ticket, manager approves, IT provisions within 2 business days").',
    evidence_tips: [
      'Control documentation library with policies and companion procedures',
      'Evidence of periodic policy reviews (version history, review dates)',
      'Records showing procedures are followed in practice (e.g., completed procedure checklists)',
    ],
    common_mistakes: 'Policies exist but have no accompanying procedures — employees understand the goal but have no guidance on how to actually implement it consistently.',
    nist_reference: 'CC5.3',
  },
  'CC5.4': {
    plain_english: 'Control activities are reviewed and updated when risks change, business processes change, technology changes, or regulations change — controls are not static artifacts.',
    example: 'After migrating to AWS, the organization reviews all controls designed for on-premises infrastructure and updates them to address cloud-specific risks like misconfigured S3 buckets.',
    evidence_tips: [
      'Change management process that includes security control review triggers',
      'Records of control reviews and updates following significant organizational changes',
      'Comparison documentation showing what changed and why controls were updated',
    ],
    common_mistakes: 'Controls are reviewed only during annual audits — major changes like cloud migrations or new product launches happen without triggering a control review.',
    nist_reference: 'CC5.4',
  },
  'CC6.1': {
    plain_english: 'The organization implements logical access controls — logins, authentication, and authorization — to ensure only authorized users can access systems and data.',
    example: 'All systems use Active Directory authentication. Role-based access groups restrict access to sensitive data. MFA is required for all cloud administrative access.',
    evidence_tips: [
      'Identity and access management configuration documentation',
      'Screenshot of RBAC group structure and access permissions',
      'MFA enrollment records showing coverage across user population',
    ],
    common_mistakes: 'Multiple people share a single "admin" login, making it impossible to audit who accessed what or hold anyone accountable for unauthorized changes.',
    nist_reference: 'CC6.1',
  },
  'CC6.2': {
    plain_english: 'New users must go through a formal registration and authorization process before receiving access. Access is provisioned only after proper approval, not just because someone asked.',
    example: 'A new employee\'s manager submits a formal access request in the ticketing system. IT only provisions access after verifying manager approval and confirming the business justification.',
    evidence_tips: [
      'Access request workflow documentation (ticketing system, forms, email approvals)',
      'Sample access request tickets showing approval chain before provisioning',
      'Onboarding checklist showing access provisioning step requires manager sign-off',
    ],
    common_mistakes: 'IT provisions access based on verbal requests or Slack messages without formal approval — there is no audit trail confirming who authorized access and why.',
    nist_reference: 'CC6.2',
  },
  'CC6.3': {
    plain_english: 'Access is modified or removed promptly when a user\'s role changes or they leave the organization. Regular access reviews catch and remediate access that is no longer appropriate.',
    example: 'When an employee transfers to a different department, their old access is revoked the same day and new access appropriate to the new role is provisioned. Quarterly access reviews identify and remove stale permissions.',
    evidence_tips: [
      'Access review reports showing periodic reviews were conducted',
      'Offboarding checklist with access termination verification',
      'Records showing access was revoked for terminated employees within defined timeframe',
    ],
    common_mistakes: 'Former employees\' accounts remain active for weeks or months after termination because there is no automated offboarding process connecting HR to IT.',
    nist_reference: 'CC6.3',
  },
  'CC6.4': {
    plain_english: 'Physical access to server rooms, data centers, and other areas housing critical systems is restricted to authorized personnel using physical controls like badge access, locks, and cameras.',
    example: 'The server room requires badge access and logs all entries. Visitor access requires escort by an authorized employee and is logged in a visitor register.',
    evidence_tips: [
      'Badge access system configuration showing who has access to secure areas',
      'Physical access log records showing entry/exit history',
      'Video surveillance coverage documentation for sensitive physical areas',
    ],
    common_mistakes: 'The server room door has a combination lock but the combination has not been changed in years and is known by many former employees.',
    nist_reference: 'CC6.4',
  },
  'CC6.5': {
    plain_english: 'When systems or storage media are retired or repurposed, data is rendered unrecoverable using approved sanitization methods like cryptographic erasure, secure wiping, or physical destruction.',
    example: 'Before a decommissioned server is sent to surplus, IT uses DBAN to perform a seven-pass wipe and documents the sanitization with a certificate of destruction.',
    evidence_tips: [
      'Media sanitization or destruction certificates for decommissioned equipment',
      'Data disposal policy specifying approved sanitization methods',
      'Inventory records linking decommissioned assets to destruction documentation',
    ],
    common_mistakes: 'Old laptops are donated or sold without wiping the hard drives — sensitive business data can be recovered by the recipient using freely available tools.',
    nist_reference: 'CC6.5',
  },
  'CC6.6': {
    plain_english: 'The organization deploys perimeter defenses — firewalls, IDS/IPS, and other boundary controls — to protect systems from external threats.',
    example: 'A next-gen firewall blocks all inbound connections except on approved ports. An IPS monitors for and blocks known attack signatures. A DMZ separates public-facing servers from internal systems.',
    evidence_tips: [
      'Firewall configuration documentation showing default-deny rules and allowed exceptions',
      'IDS/IPS deployment records and sample alert reports',
      'Network diagram showing DMZ architecture and boundary protection points',
    ],
    common_mistakes: 'Firewall rules accumulate over years with rules never removed — the firewall is effectively wide open because every past business exception is still allowed.',
    nist_reference: 'CC6.6',
  },
  'CC6.7': {
    plain_english: 'Data transmission and movement is protected — data in transit is encrypted and controls restrict who can move sensitive data and how.',
    example: 'All data transferred to third parties must use SFTP or encrypted email. A DLP tool prevents users from emailing files containing SSNs or credit card numbers to external addresses.',
    evidence_tips: [
      'Data transfer policy specifying approved methods and encryption requirements',
      'DLP tool configuration and sample alert/block events',
      'Evidence of TLS encryption for all data in transit (SSL certificates, configuration)',
    ],
    common_mistakes: 'Customer data is transferred to business partners via unencrypted email attachments because "it\'s easier," exposing sensitive data during transmission.',
    nist_reference: 'CC6.7',
  },
  'CC6.8': {
    plain_english: 'The organization prevents, detects, and responds to malicious software through anti-malware tools, application controls, integrity checking, and user awareness.',
    example: 'Endpoint protection software is deployed on all workstations and servers with real-time scanning and automatic updates. Application whitelisting prevents execution of unauthorized programs on servers.',
    evidence_tips: [
      'Endpoint protection deployment report showing coverage across all endpoints',
      'Anti-malware definition update records confirming current signatures',
      'Sample malware detection and response records',
    ],
    common_mistakes: 'Anti-malware signatures are months out of date because the update server was misconfigured — the software is running but not catching current threats.',
    nist_reference: 'CC6.8',
  },
  'CC6.9': {
    plain_english: 'Multi-factor authentication (or compensating controls of equivalent strength) protects privileged accounts and access to systems containing sensitive data.',
    example: 'All system administrators must use hardware FIDO2 keys to authenticate. All users accessing the cloud management console must complete MFA enrollment before their accounts are activated.',
    evidence_tips: [
      'MFA enforcement policy and configuration records for privileged accounts',
      'User enrollment reports showing MFA coverage for accounts with sensitive access',
      'Authentication logs showing MFA being required in practice',
    ],
    common_mistakes: 'MFA is deployed but configured with an exception for "trusted networks" — attackers who compromise an internal machine bypass MFA entirely.',
    nist_reference: 'CC6.9',
  },
  'CC7.1': {
    plain_english: 'The organization monitors for configuration changes that introduce new vulnerabilities and detects when systems drift from their secure baseline.',
    example: 'A file integrity monitoring (FIM) tool alerts when system configuration files change on production servers. A CSPM tool continuously checks cloud resources against security benchmarks.',
    evidence_tips: [
      'FIM tool deployment and configuration documentation',
      'Sample configuration drift alerts and investigation records',
      'Security baseline documentation and automated compliance scan results',
    ],
    common_mistakes: 'A developer makes a change to the production web server configuration that disables TLS 1.2 enforcement — this goes undetected for months because there is no configuration monitoring.',
    nist_reference: 'CC7.1',
  },
  'CC7.2': {
    plain_english: 'Systems and operations are continuously monitored for anomalous activity, errors, and potential compromises using SIEM tools or equivalent detection capabilities.',
    example: 'A SIEM aggregates logs from all servers, network devices, and applications. Correlation rules alert on brute force patterns, impossible travel logins, and privilege escalation events.',
    evidence_tips: [
      'SIEM architecture documentation showing log sources and coverage',
      'Sample security alerts and analyst investigation records',
      'Log source inventory confirming all critical systems feed into monitoring',
    ],
    common_mistakes: 'Log collection exists but nobody reviews the alerts — thousands of unreviewed alerts accumulate in the SIEM while an active intrusion goes unnoticed for weeks.',
    nist_reference: 'CC7.2',
  },
  'CC7.3': {
    plain_english: 'Detected events are evaluated to determine if they constitute security incidents, then classified and escalated appropriately based on their potential impact.',
    example: 'A SOC analyst investigates a SIEM alert about unusual login patterns, determines it is a credential stuffing attack against customer accounts, classifies it as a P1 incident, and escalates to the incident commander.',
    evidence_tips: [
      'Incident classification criteria and escalation procedures',
      'Security event triage records showing classification decisions and rationale',
      'Incident tracking system records with severity classifications and escalation history',
    ],
    common_mistakes: 'Everything gets classified as "low severity" because analysts lack criteria for making classification decisions and are afraid of creating work for the organization.',
    nist_reference: 'CC7.3',
  },
  'CC7.4': {
    plain_english: 'When security incidents occur, the organization responds according to a documented plan covering containment, eradication, recovery, and communication to affected parties.',
    example: 'Upon detecting ransomware, the incident commander isolates affected systems, activates the backup recovery plan, notifies customers via the breach notification template, and documents all actions in the incident ticket.',
    evidence_tips: [
      'Incident response plan document with defined roles, procedures, and communication templates',
      'Incident response exercise records showing the plan has been tested',
      'Records of past incident responses showing the plan was followed',
    ],
    common_mistakes: 'The incident response plan is a PDF document nobody has read — when an actual incident occurs, responders improvise chaotically rather than following a tested procedure.',
    nist_reference: 'CC7.4',
  },
  'CC7.5': {
    plain_english: 'After an incident, the organization restores systems to normal operations, conducts a post-incident review, and implements improvements to prevent recurrence.',
    example: 'After a phishing incident, systems are restored from clean backups, a root cause analysis identifies that email filtering was bypassed, and DMARC enforcement is implemented to prevent recurrence.',
    evidence_tips: [
      'Post-incident review (PIR) or after-action report documenting lessons learned',
      'Evidence of remediations implemented following incidents',
      'Updated procedures or controls resulting from incident lessons learned',
    ],
    common_mistakes: 'After an incident is contained, the team declares victory and moves on — no lessons-learned review is conducted, so the same vulnerability is exploited again within months.',
    nist_reference: 'CC7.5',
  },
  'CC7.6': {
    plain_english: 'The organization has a patch management process that identifies, tests, and applies security patches within defined timeframes based on risk, keeping systems protected against known vulnerabilities.',
    example: 'Critical CVEs are patched within 14 days. High CVEs within 30 days. Patches are tested in staging before production deployment. A vulnerability scan verifies patches are applied.',
    evidence_tips: [
      'Patch management policy with defined remediation timeframes by severity',
      'Vulnerability scan reports before and after patching cycles',
      'Patch deployment records confirming patches were applied within SLA',
    ],
    common_mistakes: 'Patching is ad-hoc — there is no defined schedule, no tracking of open vulnerabilities, and no SLA for remediation. Critical vulnerabilities sit unpatched for months.',
    nist_reference: 'CC7.6',
  },
  'CC8.1': {
    plain_english: 'All changes to systems, software, and infrastructure go through a formal change management process: authorization, design, testing, approval, and deployment with rollback capability.',
    example: 'A database schema change requires a change request ticket, technical design review, testing in staging, CAB approval, deployment during a maintenance window, and a rollback script on standby.',
    evidence_tips: [
      'Change management policy and process documentation',
      'Sample approved change tickets showing the full approval workflow',
      'Evidence of rollback procedures and their use when changes fail',
    ],
    common_mistakes: 'Developers push changes directly to production without any approval process because "it\'s a small change" — eventually a "small change" causes an outage or introduces a vulnerability.',
    nist_reference: 'CC8.1',
  },
  'CC8.2': {
    plain_english: 'Customers and stakeholders are notified in advance when planned changes could impact them, giving them time to prepare.',
    example: 'Planned maintenance windows are announced to customers via email 72 hours in advance. A status page shows the scheduled maintenance window and is updated in real time during the event.',
    evidence_tips: [
      'Customer notification records for planned maintenance windows',
      'Status page or communication platform showing advance notifications',
      'Change management process documentation requiring customer notification steps',
    ],
    common_mistakes: 'A major database maintenance window is performed without notifying customers — their applications break during the window and they have no context for why.',
    nist_reference: 'CC8.2',
  },
  'CC9.1': {
    plain_english: 'The organization has documented business continuity plans to maintain operations and recover from disruptions, disasters, and major outages.',
    example: 'A documented BCP covers scenarios including office fire, cloud provider outage, and key person loss. Recovery time objectives (RTOs) are defined for each critical system.',
    evidence_tips: [
      'Business continuity plan document with RTO/RPO definitions for critical systems',
      'BCP test or tabletop exercise records within the past 12 months',
      'Business impact analysis identifying critical functions and dependencies',
    ],
    common_mistakes: 'The organization has backups but no tested recovery procedure — when a disaster occurs, it takes days to figure out how to restore systems from those backups.',
    nist_reference: 'CC9.1',
  },
  'CC9.2': {
    plain_english: 'The organization assesses the security posture of vendors and business partners before engaging them and monitors compliance with security requirements on an ongoing basis.',
    example: 'Before onboarding a new SaaS vendor with access to customer data, the security team reviews their SOC 2 report, conducts a security questionnaire, and includes security requirements in the contract.',
    evidence_tips: [
      'Vendor security assessment questionnaires and responses',
      'Third-party security certifications (SOC 2, ISO 27001) for key vendors',
      'Contract or DPA language including security requirements and audit rights',
    ],
    common_mistakes: 'Vendors are selected based on price and features with no security evaluation — a vendor breach later exposes customer data because no contractual security obligations existed.',
    nist_reference: 'CC9.2',
  },
  'CC9.3': {
    plain_english: 'The organization maintains cyber insurance, contractual indemnification, or other financial risk transfer mechanisms to reduce the financial impact of security incidents.',
    example: 'The organization holds a $5M cyber liability insurance policy covering breach notification costs, ransomware response, and third-party liability. The policy is reviewed annually.',
    evidence_tips: [
      'Cyber insurance policy documentation with coverage limits and scope',
      'Evidence of annual policy review and coverage adequacy assessment',
      'Contractual indemnification clauses in customer and vendor agreements',
    ],
    common_mistakes: 'The organization has general business insurance but no cyber-specific coverage — a breach results in millions in response costs with no insurance coverage to offset them.',
    nist_reference: 'CC9.3',
  },
  'A1.1': {
    plain_english: 'The organization monitors system performance and capacity to ensure it can meet availability commitments. Demand is forecasted and capacity is added before shortfalls occur.',
    example: 'Database CPU and disk utilization are monitored with automated alerts at 80% threshold. Capacity reports are reviewed monthly, and additional resources are provisioned when 3-month projections show risk of capacity exhaustion.',
    evidence_tips: [
      'Capacity monitoring dashboards and configuration',
      'Capacity planning reports and resource utilization trend data',
      'Evidence of proactive capacity additions before commitments were at risk',
    ],
    common_mistakes: 'Capacity is only addressed reactively — systems reach 100% utilization and crash, causing outages rather than being expanded before the limit is reached.',
    nist_reference: 'A1.1',
  },
  'A1.2': {
    plain_english: 'The organization has designed infrastructure with redundancy and documented recovery procedures to maintain availability commitments even when components fail.',
    example: 'The web application runs across multiple availability zones. Automated backups run daily with 30-day retention. A documented DR runbook covers recovery from database failure within the 4-hour RTO.',
    evidence_tips: [
      'Infrastructure architecture diagram showing redundancy and failover capabilities',
      'Backup configuration and backup verification records',
      'Disaster recovery runbook with documented procedures and tested RTOs',
    ],
    common_mistakes: 'Backups exist but have never been tested for recovery — when data loss occurs, it is discovered the backups are corrupt or incomplete.',
    nist_reference: 'A1.2',
  },
  'A1.3': {
    plain_english: 'Recovery and continuity procedures are tested at least annually to verify they work and that systems can be restored within the committed timeframes.',
    example: 'The organization performs a full DR exercise annually, restoring the production database to the DR environment and measuring actual recovery time against the RTO. Results and gaps are documented.',
    evidence_tips: [
      'DR test or tabletop exercise reports from within the past 12 months',
      'Test results documenting actual recovery times versus RTO targets',
      'Records of issues identified during testing and subsequent remediation',
    ],
    common_mistakes: 'The DR procedure document is updated but never tested — the first real test happens during an actual disaster, revealing that the procedure is outdated and incomplete.',
    nist_reference: 'A1.3',
  },
  'A1.4': {
    plain_english: 'Availability metrics like uptime, latency, and error rates are monitored and reported to demonstrate that the organization is meeting its availability commitments.',
    example: 'A status page shows real-time and historical uptime percentages. Monthly availability reports are shared with enterprise customers. SLA breach reports trigger management review.',
    evidence_tips: [
      'Monitoring dashboards showing uptime, error rates, and latency metrics',
      'Availability/SLA compliance reports shared with customers or management',
      'Evidence of alerting when availability metrics degrade below thresholds',
    ],
    common_mistakes: 'Availability is only tracked when customers complain — there is no proactive monitoring, so SLA breaches are discovered after the fact rather than prevented.',
    nist_reference: 'A1.4',
  },
  'PI1.1': {
    plain_english: 'The organization provides customers and users with accurate, complete information about how the system processes data, including input specifications, processing logic, and outputs.',
    example: 'API documentation clearly describes accepted input formats, validation rules, and output data structures. Customer contracts include data processing specifications.',
    evidence_tips: [
      'System documentation or API specs describing data processing specifications',
      'Customer contracts or service descriptions that include processing commitments',
      'Data flow diagrams showing inputs, processing steps, and outputs',
    ],
    common_mistakes: 'Processing specifications are only in developers\' heads — customers cannot verify what happens to their data because there is no formal documentation.',
    nist_reference: 'PI1.1',
  },
  'PI1.2': {
    plain_english: 'System boundaries, components, and data flows are clearly documented so customers understand exactly what is and is not in scope for processing integrity commitments.',
    example: 'The system description document clearly defines which data streams are covered by the SOC 2 report, which components process customer data, and which third-party integrations are included.',
    evidence_tips: [
      'System description document clearly defining in-scope components and boundaries',
      'Data flow diagrams showing all processing paths within the defined scope',
      'SOC 2 system description or equivalent boundary documentation',
    ],
    common_mistakes: 'The system scope is vague — customers assume a vendor\'s SOC 2 covers a component that is actually excluded, leading to mismatched expectations about data protection.',
    nist_reference: 'PI1.2',
  },
  'PI1.3': {
    plain_english: 'Input validation controls ensure only accurate, complete, and authorized data enters the system — invalid or malicious inputs are rejected.',
    example: 'Web forms validate data types, field lengths, and format before accepting input. API endpoints reject requests that fail schema validation with clear error messages.',
    evidence_tips: [
      'Input validation rules and code review evidence',
      'System error logs showing invalid input rejection',
      'Security testing results confirming input validation controls are effective',
    ],
    common_mistakes: 'Input validation is done only on the client side (JavaScript) which can be bypassed — server-side validation is absent, allowing malformed or malicious data to enter the database.',
    nist_reference: 'PI1.3',
  },
  'PI1.4': {
    plain_english: 'System outputs are delivered completely, accurately, and on time according to service commitments. Output completeness and accuracy are verified.',
    example: 'Reports generated by the system include checksums so recipients can verify completeness. Delivery time SLAs are monitored and breaches trigger alerts.',
    evidence_tips: [
      'Output quality control procedures and verification records',
      'SLA monitoring for output delivery timeliness',
      'Reconciliation or completeness verification records for critical outputs',
    ],
    common_mistakes: 'Reports are generated and sent without any verification of completeness — a bug truncates reports and customers make decisions based on incomplete data without realizing it.',
    nist_reference: 'PI1.4',
  },
  'PI1.5': {
    plain_english: 'Data in storage — inputs, data being processed, and outputs — is protected from unauthorized alteration or deletion throughout its lifecycle.',
    example: 'Database records include audit timestamps and are protected by access controls. Write-once storage is used for final outputs. Integrity hashes are computed and stored for critical data files.',
    evidence_tips: [
      'Database access controls showing restrictions on who can modify records',
      'Integrity verification mechanisms (checksums, digital signatures) for stored data',
      'Audit log records showing data access and modification history',
    ],
    common_mistakes: 'Database records have no integrity verification — data could be modified by privileged users or through SQL injection and there is no way to detect the tampering.',
    nist_reference: 'PI1.5',
  },
  'PI1.6': {
    plain_english: 'Processing errors and anomalies are detected, logged, investigated, and corrected — affected parties are notified when required.',
    example: 'Automated monitoring detects when batch processing job output counts differ from input counts. Anomalies trigger alerts, investigation tickets are created, and customers are notified if their data was affected.',
    evidence_tips: [
      'Processing error monitoring and alerting configuration',
      'Error investigation records and root cause analysis documentation',
      'Customer notification records for data processing errors that affected them',
    ],
    common_mistakes: 'Processing errors are silently swallowed by the system — errors are logged but never reviewed, and affected customers are never notified.',
    nist_reference: 'PI1.6',
  },
  'C1.1': {
    plain_english: 'The organization identifies what information is confidential and implements appropriate controls to protect it throughout its lifecycle — creation, use, storage, sharing, and deletion.',
    example: 'A data classification policy designates customer PII and trade secrets as "Confidential." Confidential data is encrypted at rest, access is restricted to authorized users, and handling procedures specify approved transmission methods.',
    evidence_tips: [
      'Data classification policy with clear definition of confidential information categories',
      'Evidence that confidential data is identified and labeled in systems',
      'Access control records showing restrictions on confidential data access',
    ],
    common_mistakes: 'Confidentiality commitments are made to customers but the organization has never identified which data is actually confidential, so no controls have been applied.',
    nist_reference: 'C1.1',
  },
  'C1.2': {
    plain_english: 'When confidential information is no longer needed, it is disposed of securely — permanently deleted, anonymized, or physically destroyed so it cannot be recovered.',
    example: 'When a customer contract ends, their data is securely deleted within 30 days per the contractual retention schedule. Deletion is documented and the customer receives a certificate of destruction.',
    evidence_tips: [
      'Data retention and deletion schedule with documented timelines',
      'Secure deletion execution records or certificates of destruction',
      'Customer contracts specifying data deletion timelines and evidence of compliance',
    ],
    common_mistakes: 'Data is retained indefinitely "just in case" — old customer data from terminated contracts sits in systems for years after the relationship ended, creating unnecessary liability.',
    nist_reference: 'C1.2',
  },
  'C1.3': {
    plain_english: 'A formal data classification scheme defines categories of information with appropriate handling, access, storage, transmission, and retention controls for each level.',
    example: 'The classification scheme defines Public, Internal, Confidential, and Restricted tiers. Restricted data requires encryption at rest, MFA for access, and secure destruction on deletion.',
    evidence_tips: [
      'Data classification policy defining all tiers and their handling requirements',
      'Evidence that classification is applied to data in systems (labels, metadata)',
      'Training records showing employees understand and apply the classification scheme',
    ],
    common_mistakes: 'A classification scheme is documented but never implemented — all data is handled identically regardless of its classification, negating the purpose of the policy.',
    nist_reference: 'C1.3',
  },
  'P1.1': {
    plain_english: 'Individuals are given clear, accessible notice about what personal data is collected, why it is collected, how it is used, with whom it is shared, how long it is kept, and how they can exercise their rights.',
    example: 'A privacy notice on the website explains data collection, retention periods, and user rights in plain language. Users must acknowledge the notice before signing up.',
    evidence_tips: [
      'Privacy notice or privacy policy published on the website',
      'User acknowledgment records showing the privacy notice was presented and accepted',
      'Content review confirming the privacy notice covers all required elements',
    ],
    common_mistakes: 'The privacy policy is a dense legal document written in complex language that real users cannot understand, failing to provide meaningful notice.',
    nist_reference: 'P1.1',
  },
  'P1.2': {
    plain_english: 'The organization maintains records of all data processing activities and can demonstrate compliance with privacy regulations through documented policies and evidence.',
    example: 'A Records of Processing Activities (RoPA) document catalogs all data processing activities, legal bases, retention periods, and data transfers. It is reviewed annually.',
    evidence_tips: [
      'Records of Processing Activities (RoPA) document',
      'Privacy compliance assessment or audit records',
      'Documentation of legal basis for each processing activity',
    ],
    common_mistakes: 'No records of processing activities are maintained — the organization cannot tell regulators or customers what personal data it processes and why.',
    nist_reference: 'P1.2',
  },
  'P2.1': {
    plain_english: 'Individuals are informed of their choices about how their personal data is used and consent is obtained where legally required, with mechanisms for withdrawing consent.',
    example: 'Marketing email signup includes an opt-in checkbox (not pre-checked). Users can manage their preferences and opt out at any time from account settings. Consent withdrawal requests are processed within 5 business days.',
    evidence_tips: [
      'Consent mechanisms in the product (opt-in checkboxes, consent banners)',
      'Consent log records showing when and how consent was obtained',
      'Evidence that consent withdrawal requests are honored promptly',
    ],
    common_mistakes: 'Marketing consent is pre-checked on signup forms, treating silence as consent — this violates GDPR and similar regulations requiring affirmative opt-in.',
    nist_reference: 'P2.1',
  },
  'P3.1': {
    plain_english: 'Personal data is collected only for specified, legitimate purposes with an appropriate legal basis, and only the minimum data necessary to fulfill those purposes is collected.',
    example: 'A mobile app that requires location to find nearby stores does not request contacts or camera access. The privacy notice specifies location as the purpose for location data collection.',
    evidence_tips: [
      'Data mapping documentation showing purpose and legal basis for each data element collected',
      'Privacy impact assessment for new data collection',
      'Evidence that unnecessary data fields were removed from forms or APIs',
    ],
    common_mistakes: 'Sign-up forms collect extensive personal data "because it might be useful someday" — data is collected without a defined purpose, creating unnecessary privacy risk.',
    nist_reference: 'P3.1',
  },
  'P3.2': {
    plain_english: 'Privacy impact assessments are conducted before implementing new systems, features, or integrations that collect or process personal data, identifying and mitigating privacy risks upfront.',
    example: 'Before launching a new customer analytics feature that processes browsing behavior, a PIA is conducted, privacy risks are identified and mitigated, and the feature is updated before launch.',
    evidence_tips: [
      'Privacy impact assessment template and completed PIAs for new initiatives',
      'Evidence that PIA findings were addressed before system go-live',
      'Gate process documentation requiring PIA sign-off for projects involving personal data',
    ],
    common_mistakes: 'New features are built and launched without any privacy review — privacy concerns are only discovered after launch when customers or regulators complain.',
    nist_reference: 'P3.2',
  },
  'P4.1': {
    plain_english: 'Personal data is used only for the purposes disclosed to individuals, retained only as long as necessary, and securely disposed of when the retention period expires.',
    example: 'Customer data collected for service delivery is not used for advertising without consent. Data is automatically deleted after the 3-year retention period using scheduled purge jobs.',
    evidence_tips: [
      'Data retention schedule with defined retention periods for each data type',
      'Evidence of automated or documented data deletion at retention period expiry',
      'Purpose limitation controls showing data is not used beyond disclosed purposes',
    ],
    common_mistakes: 'Customer data collected for one purpose is later shared with a marketing partner for an entirely different purpose without the customer\'s knowledge or consent.',
    nist_reference: 'P4.1',
  },
  'P4.2': {
    plain_english: 'Technical and procedural controls prevent personal data from being used for undisclosed purposes and ensure such usage would be logged and detectable.',
    example: 'Database access controls prevent the analytics team from accessing raw PII — they can only access aggregated, anonymized data. Audit logs record all access to personal data tables.',
    evidence_tips: [
      'Access control configuration restricting personal data to authorized uses',
      'Audit log configuration for personal data access',
      'Evidence that access to personal data outside authorized purposes is detected and prevented',
    ],
    common_mistakes: 'All employees with database access can query personal data tables — there are no technical controls preventing use of personal data for unauthorized purposes.',
    nist_reference: 'P4.2',
  },
  'P5.1': {
    plain_english: 'Individuals can request access to their personal data, correct inaccuracies, and (where applicable) request deletion or portability — and the organization fulfills these requests in a reasonable timeframe.',
    example: 'A privacy request portal allows users to submit data access, deletion, and correction requests. Requests are processed within 30 days. A workflow ensures acknowledgment, fulfillment, and documented response.',
    evidence_tips: [
      'Individual rights request process documentation',
      'Sample fulfilled data access, correction, or deletion requests with response times',
      'Privacy request tracking system or log',
    ],
    common_mistakes: 'Data subject requests arrive via email and are handled ad-hoc — there is no process, requests are lost, and responses are inconsistent.',
    nist_reference: 'P5.1',
  },
  'P6.1': {
    plain_english: 'Personal data is only disclosed to third parties with individual consent or as otherwise consistent with privacy commitments, applicable law, and with appropriate contractual protections.',
    example: 'Before sharing customer data with a new analytics vendor, a Data Processing Agreement (DPA) is signed, the sharing is covered by the privacy notice, and data is minimized to only what the vendor needs.',
    evidence_tips: [
      'Data Processing Agreements (DPAs) with all third-party recipients of personal data',
      'Third-party sharing disclosures in the privacy notice',
      'Records of consent or legal basis for each third-party data sharing activity',
    ],
    common_mistakes: 'Customer data is shared with marketing partners and analytics vendors without DPAs or disclosure in the privacy policy — a regulatory violation and a breach of trust.',
    nist_reference: 'P6.1',
  },
  'P7.1': {
    plain_english: 'The organization keeps personal data accurate, complete, and relevant for its intended purposes, and provides mechanisms for individuals to correct inaccurate information.',
    example: 'Users can edit their contact information and preferences in account settings. Customer service can update records based on verified correction requests. Data quality checks flag obviously erroneous entries.',
    evidence_tips: [
      'User-facing data management interfaces showing correction mechanisms',
      'Data quality monitoring processes and records',
      'Records of correction requests received and how they were handled',
    ],
    common_mistakes: 'Customer data is collected once and never updated — stale addresses and contact information remain in the system, causing delivery failures and compliance risks.',
    nist_reference: 'P7.1',
  },
  'P8.1': {
    plain_english: 'The organization has a documented process for receiving, investigating, and resolving privacy complaints and disputes from individuals and regulators, with timely responses.',
    example: 'A privacy complaint form is accessible on the website. Complaints are logged, acknowledged within 72 hours, and resolved within 30 days. Regulatory complaints receive priority handling with legal counsel involvement.',
    evidence_tips: [
      'Privacy complaint process documentation with defined response timeframes',
      'Privacy complaint log showing receipt, investigation, and resolution records',
      'Records of regulatory communications and responses',
    ],
    common_mistakes: 'Privacy complaints are forwarded to customer support and handled like feature requests — there is no privacy-specific process and complaints languish without resolution.',
    nist_reference: 'P8.1',
  },
};

export default SOC2_EXPLANATIONS;
